import { EnrichmentPriceItem } from "@app/shared/models/enrichment-detail.model";
import { isSofRule } from "@app/shared/utils/is-sof-rule.util";

export interface PriceSaveAuthoring {
  amountsUnlocked: boolean;
  unlockedQuantities: ReadonlySet<string>;
  sourceOfFundToggle: {
    edited: boolean;
    channel: string | null | undefined;
    productType: string | null | undefined;
  } | null;
}

export const NOTHING_AUTHORED: Readonly<PriceSaveAuthoring> = {
  amountsUnlocked: false,
  unlockedQuantities: new Set<string>(),
  sourceOfFundToggle: null,
};

/**
 * The BFF clears a displays, priceDiscount, customerPersona or arpu block the
 * body leaves out, so the body starts from the whole stored price.
 */
export function buildPriceSaveBody(
  price: EnrichmentPriceItem,
  authoring: PriceSaveAuthoring,
): Omit<EnrichmentPriceItem, "lifecycleStatus"> {
  const body = stripUneditableAmounts(
    stripUnauthoredQuantities(price, (displayName) =>
      authoring.unlockedQuantities.has(displayName),
    ),
    authoring.amountsUnlocked,
  );
  const toggle = authoring.sourceOfFundToggle;
  if (!toggle) return stripUntouchedIsSof(body, false);
  const seeded = seedIsSofDefault(body, toggle.channel, toggle.productType);
  return stripUntouchedIsSof(seeded.price, seeded.seeded || toggle.edited);
}

/**
 * Drop `dutyFreeAmount` / `taxIncludedAmount` from a price body unless the
 * author both holds `enrichment:publish` AND has unlocked this price.
 *
 * Both amounts round-trip: `GET /price/:id` returns whatever the POP has
 * stored, and `modelToDetail`'s `{...orig}` spread copies them onto every
 * PATCH body — so a price that already carries them re-sends them on ANY
 * save, including one that only touched a description. Absent means "inherit
 * the stored characteristic" on the BE, so dropping the pair is lossless.
 *
 * The lock is the second condition, not decoration: a locked card must ship
 * no amounts at all. "Disabled input" and "absent from the payload" are
 * different guarantees, and only the second one survives the trip
 * (`docs/adr/0001-the-price-lock-gates-the-wire.md`). `EnrichmentPricing`
 * reverts on re-lock, so in practice a locked price's amounts already equal
 * the stored ones — this makes that a property of the wire rather than of
 * the component that happens to be mounted.
 *
 * Authorization lives on the server, which gates the amounts on
 * `enrichment:publish` itself (BFF ADR 0002). This function shapes the
 * payload; it does not enforce anything.
 */
function stripUneditableAmounts(
  price: EnrichmentPriceItem,
  mayEditAmounts: boolean,
): Omit<EnrichmentPriceItem, "lifecycleStatus"> {
  const { lifecycleStatus: _priceStatus, ...body } = price;
  // Pure passthrough for an author editing the amounts — re-spreading the
  // pair would put both keys back as `undefined` on a price that never
  // carried them.
  if (mayEditAmounts) return body;
  const { dutyFreeAmount: _net, taxIncludedAmount: _gross, ...rest } = body;
  return rest;
}

/**
 * Write the default the toggle is showing for a price that has never had a
 * Source of Fund value.
 *
 * The Prices tab shows "on" for such a prepaid price. Without this the author
 * reads "on", saves, and the document still holds nothing — the screen and
 * the data disagree, and only the screen is visible to them. Seeding happens
 * only where the control is editable, and only when the value is ABSENT, so
 * it can never replace a value the catalog published: there is none to
 * replace. Returns whether it seeded, so the caller keeps the key on the wire.
 */
function seedIsSofDefault<T extends { isSof?: boolean; priceType?: string }>(
  price: T,
  channel: string | null | undefined,
  productType: string | null | undefined,
): { price: T; seeded: boolean } {
  if (typeof price.isSof === "boolean") return { price, seeded: false };
  const rule = isSofRule({ channel, productType, priceType: price.priceType });
  if (!rule.visible || !rule.editable) return { price, seeded: false };
  return { price: { ...price, isSof: rule.defaultValue }, seeded: true };
}

/**
 * Keep Source of Fund (`isSof`) off the wire unless the author changed this
 * price's toggle.
 *
 * The whole price round-trips, so without this every save would echo back the
 * value the GET returned. The backend writes any `isSof` it receives as an
 * enricher-owned entry, and such an entry outranks the catalog's own from then
 * on — saving an unrelated icon would quietly freeze a value the catalog is
 * still meant to control. "The author chose this" is therefore the only thing
 * that may put the key in the body.
 */
function stripUntouchedIsSof<T extends { isSof?: boolean }>(
  price: T,
  edited: boolean,
): T {
  if (edited || !("isSof" in price)) return price;
  const { isSof: _untouched, ...rest } = price;
  return rest as T;
}

/**
 * Decide, per display, whether `value.unitOfMeasure.amount` rides on the wire,
 * and strip the backend's read-only `catalogAmount` hint from all of them.
 *
 * The backend re-derives a bundle display's amount from the parent catalog on
 * every price save unless the payload carries the key, and the whole
 * `displays` array round-trips untouched — so BOTH directions are dangerous:
 * send every amount and each display silently stops tracking the catalog;
 * send none and an amount somebody authored last week is wiped by today's
 * save of an unrelated icon.
 *
 * What tells the two apart is the hint the backend stamps beside the figure:
 *
 * - **No `catalogAmount` key** — the backend derives nothing for this display
 *   (no parent bundle, a note shell, a stale ref). It will not touch the
 *   amount, so neither do we.
 * - **`amount` differs from `catalogAmount`** — somebody chose this figure.
 *   Keep sending it, or the next save hands the display back to the catalog
 *   without anyone asking.
 * - **`amount` equals `catalogAmount`, or is absent** — the display is still
 *   tracking. Drop the key so the backend goes on deriving it, and so a
 *   catalog change still reaches the customer.
 * - **Unlocked right now** — the author is editing it this minute; send
 *   whatever the form holds, including a `null` that means Unlimited.
 *
 * Setting a quantity to exactly the catalog's figure therefore reads as
 * "tracking" rather than "authored". That is deliberate: the stored result is
 * identical either way, and resuming tracking is the better of the two.
 */
function stripUnauthoredQuantities(
  price: EnrichmentPriceItem,
  isUnlocked: (displayName: string) => boolean,
): EnrichmentPriceItem {
  const displays = price.displays;
  if (!Array.isArray(displays) || displays.length === 0) return price;

  let changed = false;
  const next = displays.map((display) => {
    const value = display?.value;
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return display;
    }
    const valueRecord = value as Record<string, unknown>;
    const uom = valueRecord["unitOfMeasure"];
    if (!uom || typeof uom !== "object" || Array.isArray(uom)) return display;
    const uomRecord = uom as Record<string, unknown>;

    const hasHint = "catalogAmount" in uomRecord;
    const hasAmount = "amount" in uomRecord;
    const authored =
      hasAmount &&
      (isUnlocked(display.name) ||
        !hasHint ||
        !Object.is(uomRecord["amount"], uomRecord["catalogAmount"]));

    // `catalogAmount` is computed, never stored: `mergeDisplays` persists a
    // display's value opaquely, so echoing it back would write a stale copy
    // of a derived figure into the record.
    if (!hasHint && authored) return display;

    changed = true;
    const { catalogAmount: _hint, ...rest } = uomRecord;
    const nextUom = authored
      ? rest
      : (({ amount: _derived, ...keep }) => keep)(rest);
    return {
      ...display,
      value: { ...valueRecord, unitOfMeasure: nextUom },
    };
  });

  return changed ? { ...price, displays: next } : price;
}
